import { t } from "@lingui/macro";
import { useCallback, useEffect } from "react";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner";
import {
  InfoBannerFailureIcon,
  InfoBannerWarningIcon,
} from "@whoosh/design-system/src/components/display/InfoBanner";
import { IconProps } from "@whoosh/design-system/src/components/primitive";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { formatHHMM } from "@whoosh/utilities/src/dates";

import { useRequestRestrictionStatuses_booking$key } from "./hooks/__generated__/useRequestRestrictionStatuses_booking.graphql";
import {
  RequestRestrictionStatusRow,
  useHasBlockingRequestRestrictionStatus,
  useRequestRestrictionStatuses,
  useShouldShowRequestRestrictionBanners,
} from "./hooks/useRequestRestrictionStatuses";

type Props = {
  bookingRef: useRequestRestrictionStatuses_booking$key | null;
  requestGroupCount?: number;
};

const formatEntityTimeRange = (startAt: string, endAt: string): string => {
  const start = formatHHMM(startAt, true, true);
  const end = formatHHMM(endAt, true, true);

  if (start && end) {
    return `${start} - ${end}`;
  }

  return start ?? end ?? "";
};

export const RequestRestrictionBannerStack = ({
  bookingRef,
  requestGroupCount = 1,
}: Props) => {
  const shouldShow = useShouldShowRequestRestrictionBanners(bookingRef);
  const statuses = useRequestRestrictionStatuses(bookingRef);
  const hasBlockingRequestRestrictionStatus =
    useHasBlockingRequestRestrictionStatus(bookingRef);
  const trackEvent = useTrackEvent();

  useEffect(() => {
    if (
      requestGroupCount > 1 &&
      shouldShow &&
      hasBlockingRequestRestrictionStatus
    ) {
      trackEvent("MultipleSlotRequest.RestrictionOrAvailabilityFailed", {
        failureType: "restriction",
      });
    }
  }, [
    hasBlockingRequestRestrictionStatus,
    requestGroupCount,
    shouldShow,
    trackEvent,
  ]);

  if (!shouldShow || statuses.length === 0) {
    return null;
  }

  return (
    <VStack gap={1} paddingBottom={2} testID="request-restriction-banner-stack">
      {statuses.map((status) => (
        <RequestRestrictionBanner
          key={bannerKey(status)}
          status={status}
          requestGroupCount={requestGroupCount}
        />
      ))}
    </VStack>
  );
};

const bannerKey = (status: RequestRestrictionStatusRow): string =>
  `${status.type}-${status.label}-${status.startAt}-${status.endAt}-${
    status.blockingScope
  }-${status.bookingIndex ?? "request"}`;

type BannerProps = {
  status: RequestRestrictionStatusRow;
  requestGroupCount: number;
};

const RequestRestrictionBanner = ({
  status,
  requestGroupCount,
}: BannerProps) => {
  const isFull = status.blockingScope === "FULL";

  const LeadingIcon = useCallback(
    (props: IconProps) =>
      isFull ? (
        <InfoBannerFailureIcon {...props} />
      ) : (
        <InfoBannerWarningIcon {...props} />
      ),
    [isFull]
  );

  return (
    <InfoBanner
      leadingIcon={LeadingIcon}
      message={
        status.bookingIndex != null && requestGroupCount > 1
          ? t`Group ${status.bookingIndex + 1} of ${requestGroupCount}: ${
              status.label
            }`
          : status.label
      }
      secondaryText={formatEntityTimeRange(status.startAt, status.endAt)}
      colorTheme={isFull ? "ERROR" : "WARNING"}
      testID={`request-restriction-banner-${status.type.toLowerCase()}-${status.label
        .toLowerCase()
        .replace(/\s+/g, "-")}`}
    />
  );
};
